/**
 * Birthstone Collection Price
 * 
 * Updates the displayed price of birthstone products on collection pages
 * to show the combined price (chain + pendant) instead of just pendant price.
 */

(function() {
  const CHAIN_HANDLE = 'curb-chain-ketting';
  const BIRTHSTONE_HANDLE_PATTERN = 'birthstone-ketting';
  
  let chainPrice = null;
  let chainComparePrice = null;
  
  async function getChainPrice() {
    if (chainPrice !== null) return { price: chainPrice, comparePrice: chainComparePrice };
    
    try {
      const response = await fetch(`/products/${CHAIN_HANDLE}.js`);
      const product = await response.json();
      chainPrice = product.price;
      chainComparePrice = product.compare_at_price;
      return { price: chainPrice, comparePrice: chainComparePrice };
    } catch (error) {
      console.error('Could not fetch chain price:', error);
      return null;
    }
  }
  
  function formatMoney(cents) {
    return '€' + (cents / 100).toFixed(2).replace('.', ',');
  }
  
  async function updateBirthstonePrices() {
    // Find all product cards
    const productCards = document.querySelectorAll('.product-item, .product-card, [data-product-card]');
    
    for (const card of productCards) {
      // Check if this is a birthstone product
      const productLink = card.querySelector('a[href*="/products/"]');
      if (!productLink) continue;
      
      const href = productLink.getAttribute('href');
      if (!href.includes(BIRTHSTONE_HANDLE_PATTERN)) continue;
      
      // Skip the chain itself
      if (href.includes(CHAIN_HANDLE)) continue;
      
      // Skip if already processed
      if (card.classList.contains('birthstone-price-updated')) continue;
      
      // Get chain price
      const chainData = await getChainPrice();
      if (!chainData) continue;
      
      // Get pendant product data via API
      const handleMatch = href.match(/\/products\/([^?#]+)/);
      if (!handleMatch) continue;
      
      const pendantHandle = handleMatch[1];
      let pendantPrice = null;
      let pendantComparePrice = null;
      
      try {
        const response = await fetch(`/products/${pendantHandle}.js`);
        const product = await response.json();
        pendantPrice = product.price;
        pendantComparePrice = product.compare_at_price;
      } catch (error) {
        console.error('Could not fetch pendant price:', error);
        continue;
      }
      
      // Calculate combined price
      const combinedPrice = chainData.price + pendantPrice;
      
      // Calculate combined compare price if either has a sale
      let combinedComparePrice = null;
      const chainOriginal = chainData.comparePrice || chainData.price;
      const pendantOriginal = pendantComparePrice || pendantPrice;
      
      if (chainOriginal > chainData.price || pendantOriginal > pendantPrice) {
        combinedComparePrice = chainOriginal + pendantOriginal;
      }
      
      // Find price element in card
      const priceElement = card.querySelector('.product-item__price-list, .price, .product-card__price, [data-product-price]');
      if (!priceElement) continue;
      
      // Update the display
      updatePriceDisplay(card, priceElement, combinedPrice, combinedComparePrice);
    }
  }
  
  function updatePriceDisplay(card, priceElement, price, comparePrice) {
    // Mark as updated to avoid re-processing
    if (card.classList.contains('birthstone-price-updated')) return;
    card.classList.add('birthstone-price-updated');
    
    // Hide all other price elements in the card
    const allPriceElements = card.querySelectorAll('.price, .product-item__price-list, .price-list, [data-product-price], .product-item__price--compare, .product-item__price--unit, .unit-price');
    allPriceElements.forEach(el => {
      if (el !== priceElement && !el.contains(priceElement) && !priceElement.contains(el)) {
        el.style.display = 'none';
      }
    });
    
    // Also hide any sibling price elements
    const parent = priceElement.parentElement;
    if (parent) {
      const siblings = parent.querySelectorAll('.price, .product-item__price, span[class*="price"]');
      siblings.forEach(el => {
        if (el !== priceElement && !el.classList.contains('birthstone-collection-price')) {
          el.style.display = 'none';
        }
      });
    }
    
    // Create new price HTML
    let newHTML = '';
    
    if (comparePrice && comparePrice > price) {
      newHTML = `<span class="birthstone-collection-price">
        <s class="birthstone-compare-price">${formatMoney(comparePrice)}</s>
        <span class="birthstone-current-price">${formatMoney(price)}</span>
      </span>`;
    } else {
      newHTML = `<span class="birthstone-collection-price">
        <span class="birthstone-current-price birthstone-current-price--no-sale">${formatMoney(price)}</span>
      </span>`;
    }
    
    priceElement.innerHTML = newHTML;
  }
  
  // Inject styles
  function injectStyles() {
    if (document.getElementById('birthstone-collection-styles')) return;
    
    const styles = document.createElement('style');
    styles.id = 'birthstone-collection-styles';
    styles.textContent = `
      .birthstone-collection-price {
        display: inline-flex;
        align-items: center;
        flex-wrap: wrap;
      }
      
      .birthstone-compare-price {
        color: rgba(var(--text-color), 0.5);
        text-decoration: line-through;
        font-weight: 400;
      }
      
      .birthstone-current-price {
        color: #b12704;
        font-weight: 400;
      }
      
      .birthstone-current-price--no-sale {
        color: inherit;
        font-weight: inherit;
      }
      
      /* Hide unit price and secondary prices on birthstone cards */
      .birthstone-price-updated .product-item__price-info > *:not(:first-child),
      .birthstone-price-updated .product-item__price--unit,
      .birthstone-price-updated .unit-price,
      .birthstone-price-updated .price-list > *:not(:first-child) {
        display: none !important;
      }
    `;
    document.head.appendChild(styles);
  }
  
  // Initialize
  function init() {
    injectStyles();
    
    // Run on page load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', updateBirthstonePrices);
    } else {
      updateBirthstonePrices();
    }
    
    // Also run when new products are loaded (infinite scroll, filtering)
    const observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      for (const mutation of mutations) {
        if (mutation.addedNodes.length > 0) {
          shouldUpdate = true;
          break;
        }
      }
      if (shouldUpdate) {
        setTimeout(updateBirthstonePrices, 100);
      }
    });
    
    const productGrid = document.querySelector('.collection__products, .product-list, [data-product-list]');
    if (productGrid) {
      observer.observe(productGrid, { childList: true, subtree: true });
    }
  }
  
  init();
  
  console.log('Birthstone collection price updater initialized');
})();